import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconXClose from "../../../../img/icons/x-close.svg";
import { focusElement } from "../../../utils/keyboardFocus";
import { IUpsellModalProps } from "../models";
import { UpsellModalSlideCollection } from "./UpsellModalSlideCollection";
import { UpsellBlockValue } from "./types";

export type { UpsellBlockValue } from "./types";
export { extractUpsellBlock } from "./types";

type IUpsellModalBlockProps = IUpsellModalProps &
    Immutable<{
        blockData: UpsellBlockValue;
    }>;

export const UpsellModal = (props: IUpsellModalBlockProps): React.ReactNode => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    const openModal = (event?: React.MouseEvent<HTMLElement>) => {
        if (event) {
            event.preventDefault();
        }
        const document = getDocument();
        if (document) {
            document.body.setAttribute("style", "overflow-y: hidden");
        }
        setModalIsOpen(true);
    };

    const closeModal = (
        event?: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ) => {
        if (event) {
            event.preventDefault();
        }
        const document = getDocument();
        if (document) {
            document.body.setAttribute("style", "overflow: initial");
        }
        setModalIsOpen(false);
        props.onProceed();
    };

    React.useImperativeHandle(
        props.ref,
        () => ({
            openModal,
        }),
        [openModal],
    );

    const slides = props.blockData.slides ?? [];

    const window = getWindow();
    const windowWidth = window?.innerWidth || 0;

    const modalStyle = {
        overlay: {
            backgroundColor: "rgba(0,0,0,0.8)",
        },
        content: {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            margin: "auto",
            overflow: windowWidth < 500 ? "scroll" : "hidden",
            boxShadow: "none",
            border: 0,
        },
    };

    const classes = classNames({
        "upsell-modal": windowWidth >= 500,
        "upsell-modal-phone": windowWidth < 500,
        "al-upsell-modal": true,
    });

    return (
        <div>
            <Modal
                aria={{ modal: true }}
                contentLabel={t`You might also like this product.`}
                className={classes}
                overlayClassName={"upsell-modal__overlay"}
                htmlOpenClassName={"upsell-modal__html-overlay"}
                style={modalStyle}
                isOpen={modalIsOpen}
                onAfterOpen={() => {
                    focusElement(".pdp-modal__close");
                }}
                onRequestClose={(e) => {
                    closeModal(e);
                }}
                role="dialog"
            >
                <button
                    aria-label={t`Close`}
                    className="pdp-modal__close"
                    onClick={(e) => {
                        closeModal(e);
                    }}
                >
                    <SVG
                        aria-hidden="true"
                        className="pdp-modal__close-icon"
                        src={iconXClose}
                        title={t`Close Icon`}
                    />
                </button>
                <div className="upsell-modal__container">
                    <div className="upsell-modal__header">
                        <span className="upsell-modal__header-copy upsell-modal__header-copy--desktop">
                            <h3 className="upsell-modal__title">
                                {props.blockData.header_title}
                            </h3>
                            <p className="upsell-modal__title-copy">
                                {props.blockData.header_copy}
                            </p>
                        </span>
                        <span className="upsell-modal__header-copy upsell-modal__header-copy--mobile">
                            <h3 className="upsell-modal__title">
                                {props.blockData.header_title_mobile}
                            </h3>
                            <p className="upsell-modal__title-copy">
                                {props.blockData.header_copy_mobile}
                            </p>
                        </span>
                    </div>
                    <UpsellModalSlideCollection
                        slides={slides}
                        bundles={props.bundles}
                        onProceed={props.onProceed}
                    />
                    <div className="upsell-modal__cta">
                        <a
                            href="#"
                            className="upsell-modal__cta-button"
                            onClick={props.onProceed}
                        >
                            {props.blockData.cta_copy}
                        </a>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
